import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { auth, functions } from '../firebase';
import { Mail, UserRound } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';

function CreateAccountCard() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('initial');
  const [showInputRing, setShowInputRing] = React.useState(false);
  const [placeholderText, setPlaceholderText] = React.useState("Deine Email...");
  const { user } = useAuth();
  const sessionId = localStorage.getItem('plottie_session_id');

  // Add effect to handle migration when user signs in
  useEffect(() => {
    const migrateUserStories = async () => {
      if (user && sessionId) {
        try {
          const migrateStories = httpsCallable(functions, 'migrateStories');
          await migrateStories({ sessionId });
          console.log('Stories migrated successfully');
        } catch (error) {
          console.error('Error migrating stories:', error);
        }
      }
    };

    migrateUserStories();
  }, [user, sessionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setShowInputRing(true);
      setPlaceholderText("Bitte gib deine Email ein.");
      setTimeout(() => {
        setShowInputRing(false);
        setPlaceholderText("Deine Email...");
      }, 1230);
      return;
    }

    setStatus('sending');
  
    const actionCodeSettings = {
      url: `${window.location.origin}/meine-geschichten?email=${encodeURIComponent(email)}`,
      handleCodeInApp: true,
    };
  
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      localStorage.setItem('auth_email_for_sign_in', email);
      setStatus('sent');
      setEmail('');
    } catch (error) {
      console.error('Error sending sign-in link', error);
      setStatus('error');
    }
  };

  return (
    <div className="w-full pt-16 lg:pt-0">
      {/* Decorative image with absolute positioning */}
      <div className="absolute w-full">
        <img 
          src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Section-Banner_Bottom-Orange-dyTvkASnVbaDA9lES0AomaVIvb4tlC.png"
          className="md:hidden w-full pointer-events-none select-none translate-y-[-99%]"
          aria-hidden="true"
          alt=""
        />
        <img 
          src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Section-Banner_Top-Orange-W2ZYP9ED9O6V7uhXp0vrc5HbOeYJHe.png"
          className="hidden md:block w-full pointer-events-none select-none rotate-180 translate-y-[-99%]"
          aria-hidden="true"
          alt=""
        />
      </div>

      {/* Content */}
      <div className="bg-[#FFA23E] px-4 pb-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[#343434] text-xl md:text-2xl font-freeman mb-1 tracking-[-0.03rem] text-center"
        >
          Sichere dir deine Geschichten
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[#343434] text-sm md:text-lg font-aleo mb-2 md:mb-4 text-center"
        >
          Erstelle jetzt einen kostenlosen Account und behalte <br /> alle deine Geschichten für immer.
        </motion.p>

        <form onSubmit={handleSubmit} className="space-y-4 max-w-3xl md:max-w-xl mx-auto">
          <AnimatePresence mode="wait">
            {status === "sent" ? (
              <motion.div
                className="h-14 px-6 py-4 rounded-full bg-[#FFF3DD] text-[#343434] text-center flex items-center justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <Mail className="w-5 h-5 mr-2" />
                <span>Check deine E-Mails!</span>
              </motion.div>
            ) : (
              <motion.div
                className="flex flex-col sm:flex-row gap-2"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={placeholderText}
                  className={`h-14 px-6 py-4 rounded-full bg-[#FFF3DD] text-[#343434] text-center outline-none flex-1 ${
                    showInputRing ? 'ring-4' : ''
                  } ring-[#FFA23E] transition-all duration-300`}
                  disabled={status === "sending"}
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="px-5 py-3 bg-[#7785FC] text-[#FFF3DD] rounded-full font-freeman text-lg tracking-[-0.03rem] hover:bg-[#f4f4f4] transition-all duration-300 flex items-center justify-center"
                >
                  <UserRound className="w-5 h-5 mr-1" />
                  Account erstellen
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </div>
  );
}

export default CreateAccountCard; 